import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  IconButton,
  Badge,
  Typography,
  Radio,
  RadioGroup,
  Button,
  Paper,
  Grid,
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import { useSsoCart } from "../../../../contexts/SsoCartContext";
import "./PurchaseAddInsDetails.css";

export default function PurchaseAddInsDetails({
  addIn,
  cartItems,
  initialSelectedSubscriptionId,
  onBack,
  onOpenCart,
  onSelectionChange,
  onAddToCart,
  onUpdateCart,
  onRemoveFromCart,
}) {
  const { count, getCartDetails } = useSsoCart();
  const [selectedSubscriptionId, setSelectedSubscriptionId] = useState(null);

  const cartEntry = (cartItems || []).find(
    (c) => String(c?.itemId ?? c?.id ?? c?.addInId) === String(addIn?.addInId),
  );
  const inCart = Boolean(cartEntry);
  const inCartSubscriptionId =
    cartEntry?.selectedSubscription?.addInSubscriptionId ??
    cartEntry?.addInSubscriptionId ??
    cartEntry?.subscriptionId ??
    null;
  const hasMultipleSubscriptionOptions =
    Array.isArray(addIn?.availableSubscriptions) &&
    addIn.availableSubscriptions.length > 1;
  const canUpdateInCart =
    inCart && addIn?.costType !== 1 && hasMultipleSubscriptionOptions;
  const showPrimaryActionButton = !inCart || canUpdateInCart;

  useEffect(() => {
    // Load cart details to update cart  count
    if (getCartDetails) {
      getCartDetails();
    }
  }, []);

  useEffect(() => {
    // Selection priority: explicit initial selection > in-cart selection > first plan
    if (addIn) {
      if (addIn.costType === 1) {
        setSelectedSubscriptionId("free");
      } else if (
        initialSelectedSubscriptionId &&
        Array.isArray(addIn.availableSubscriptions) &&
        addIn.availableSubscriptions.some(
          (sub) =>
            String(sub.addInSubscriptionId) ===
            String(initialSelectedSubscriptionId),
        )
      ) {
        setSelectedSubscriptionId(initialSelectedSubscriptionId);
      } else if (inCartSubscriptionId) {
        setSelectedSubscriptionId(inCartSubscriptionId);
      } else if (
        Array.isArray(addIn.availableSubscriptions) &&
        addIn.availableSubscriptions.length > 0
      ) {
        setSelectedSubscriptionId(
          addIn.availableSubscriptions[0].addInSubscriptionId,
        );
      } else {
        setSelectedSubscriptionId(null);
      }
    }
  }, [addIn, inCartSubscriptionId, initialSelectedSubscriptionId]);

  // Sort subscriptions by frequency value (memoized to avoid re-computation)
  const sortedSubscriptions = useMemo(() => {
    if (!Array.isArray(addIn?.availableSubscriptions)) {
      return [];
    }
    return [...addIn.availableSubscriptions].sort(
      (a, b) => (a.frequency ?? 99) - (b.frequency ?? 99)
    );
  }, [addIn?.availableSubscriptions]);

  if (!addIn) return null;

  const getSelectedSubscription = () => {
    if (addIn.costType === 1) {
      return {
        addInSubscriptionId: "free",
        actualValue: 0,
        type: 99,
        isRecurring: false,
      };
    }

    if (!Array.isArray(addIn.availableSubscriptions)) {
      return null;
    }

    return (
      addIn.availableSubscriptions.find(
        (sub) =>
          String(sub.addInSubscriptionId) === String(selectedSubscriptionId),
      ) || null
    );
  };

  const buildCartPayload = (selectedSubscription) => {
    if (addIn.costType === 1) {
      return {
        id: addIn.addInId,
        costType: 1,
        price: "$0",
      };
    }

    return {
      id: addIn.addInId,
      costType: addIn.costType,
      defaultSubscription: selectedSubscription,
    };
  };

  const isAddDisabled = () => {
    if (!inCart) return false;
    if (!canUpdateInCart) return true;
    return String(inCartSubscriptionId) === String(selectedSubscriptionId);
  };

  const handleChange = (e) => {
    const val = e.target.value;
    // radio values for paid subscriptions are numeric ids; keep type consistent
    const selectedValue = val === "free" ? "free" : Number(val);
    setSelectedSubscriptionId(selectedValue);
    onSelectionChange?.(addIn.addInId, selectedValue);
  };

  const selectSubscription = (subscriptionId) => {
    const selectedValue =
      subscriptionId === "free" ? "free" : Number(subscriptionId);
    setSelectedSubscriptionId(selectedValue);
    onSelectionChange?.(addIn.addInId, selectedValue);
  };

  const handleAddClick = async () => {
    if (!onAddToCart) return;

    const selectedSubscription = getSelectedSubscription();
    if (addIn.costType !== 1 && !selectedSubscription) {
      return;
    }

    const payload = buildCartPayload(selectedSubscription);

    if (inCart) {
      if (!canUpdateInCart) {
        return;
      }
      // update cart item if selection changed
      if (String(inCartSubscriptionId) !== String(selectedSubscriptionId)) {
        await onUpdateCart?.(addIn, selectedSubscription, payload);
      }
      return;
    }

    await onAddToCart(addIn, selectedSubscription, payload);
  };

  const handleRemoveClick = async () => {
    await onRemoveFromCart?.(addIn);
  };

  const getButtonLabel = () => {
    if (!inCart) return "Add to Cart";
    if (!canUpdateInCart) return "In Cart";
    return "Update Cart";
  };

  const renderSubscriptionOption = (sub) => {
    const price = sub.actualValue ?? sub.baseValue ?? 0;
    const frequency = sub.frequency;
    const frequencyText = (() => {
      switch (frequency) {
        case 1:
          return "Monthly";
        case 2:
          return "Quarterly";
        case 3:
          return "Semi-Annual";
        case 4:
          return "Annual";
        case 99:
          return "One Time";
        default:
          return "Unknown";
      }
    })();

    const renewalType = sub.isRecurring
      ? "Auto-renewable"
      : "Non auto-renewable";
    const isSelected =
      String(selectedSubscriptionId) === String(sub.addInSubscriptionId);

    return (
      <Box
        key={sub.addInSubscriptionId}
        className={`subscription-option ${isSelected ? "selected" : ""}`}
        sx={{ mb: 1, borderRadius: 1, p: 1 }}
        onClick={() => selectSubscription(sub.addInSubscriptionId)}
      >
        <Box className="subscription-grid">
          <Box className="radio-cell">
            <Radio value={sub.addInSubscriptionId} checked={isSelected} />
          </Box>

          <Typography className="subscription-label">Cost</Typography>
          <Typography className="detail-subscription-value">
            ${Number(price).toFixed(2)}
          </Typography>

          <Box className="radio-spacer" />
          <Typography className="subscription-label">Frequency</Typography>
          <Typography className="detail-subscription-value">
            {frequencyText}
          </Typography>

          <Box className="radio-spacer" />
          <Typography className="subscription-label">Renewal Type</Typography>
          <Typography className="detail-subscription-value">
            {renewalType}
          </Typography>

          <Box className="radio-spacer" />
          <Typography className="subscription-label">Trial</Typography>
          <Typography className="detail-subscription-value">
            {sub.enableTrial && sub.trialDurationDays
              ? `${sub.trialDurationDays} Days Trial`
              : "N/A"}
          </Typography>
        </Box>
      </Box>
    );
  };

  return (
    <Box className="addins-details-container" sx={{ p: 2 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <IconButton aria-label="back" onClick={onBack} sx={{ color: "white" }}>
          <ArrowBackIosNewIcon />
        </IconButton>
        <IconButton aria-label="open-cart" onClick={onOpenCart}>
          <Badge badgeContent={count} color="error" showZero>
            <ShoppingCartOutlinedIcon sx={{ color: "white" }} />
          </Badge>
        </IconButton>
      </Box>

      <Grid container spacing={2} alignItems="center" sx={{ mb: 3, mt: 2 }}>
        <Grid item xs={12}>
          <Typography variant="h5">{addIn.name}</Typography>
          <Typography variant="subtitle1">{addIn.vendorName}</Typography>
        </Grid>

        <Grid item xs={12}>
          <Paper variant="outlined" className="paper-card">
            <Typography sx={{ mt: 1 }}>Description</Typography>
            <Typography sx={{ mt: 1 }}>{addIn.addInDescription}</Typography>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper variant="outlined" className="paper-card">
            <Typography sx={{ mt: 1 }}>Type</Typography>
            <Typography sx={{ mt: 1 }}>Individual</Typography>
          </Paper>
        </Grid>

        {addIn.costType !== 1 && (
          <Grid item xs={12}>
            <Paper variant="outlined" className="paper-card">
              <Typography variant="subtitle1">Subscription Details</Typography>
              <RadioGroup
                value={selectedSubscriptionId}
                onChange={handleChange}
              >
                {sortedSubscriptions.length > 0 ? (
                  sortedSubscriptions.map((sub) => renderSubscriptionOption(sub))
                ) : (
                  <Typography>No subscription plans available</Typography>
                )}
              </RadioGroup>
            </Paper>
            <Typography variant="caption" sx={{ fontStyle: "italic" }}>
              Subscription plan can be changed in Cart before checkout.
            </Typography>
          </Grid>
        )}
        <Grid item xs={12}>
          <Box display="flex" gap={1}>
            {showPrimaryActionButton && (
              <Button
                variant="contained"
                color="primary"
                onClick={handleAddClick}
                disabled={isAddDisabled()}
                fullWidth
                className="addins-add-to-cart"
              >
                {getButtonLabel()}
              </Button>
            )}
            {inCart && (
              <Button
                variant="outlined"
                color="error"
                onClick={handleRemoveClick}
                fullWidth
              >
                Remove
              </Button>
            )}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}

PurchaseAddInsDetails.propTypes = {
  addIn: PropTypes.shape({
    addInId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
      .isRequired,
    name: PropTypes.string,
    vendorName: PropTypes.string,
    addInDescription: PropTypes.string,
    costType: PropTypes.number,
    availableSubscriptions: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
  cartItems: PropTypes.arrayOf(PropTypes.object),
  initialSelectedSubscriptionId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  onBack: PropTypes.func.isRequired,
  onOpenCart: PropTypes.func,
  onSelectionChange: PropTypes.func,
  onAddToCart: PropTypes.func,
  onUpdateCart: PropTypes.func,
  onRemoveFromCart: PropTypes.func,
};

PurchaseAddInsDetails.defaultProps = {
  cartItems: [],
  initialSelectedSubscriptionId: null,
  onOpenCart: () => {},
  onSelectionChange: () => {},
  onAddToCart: () => {},
  onUpdateCart: () => {},
  onRemoveFromCart: () => {},
};
